// ABOUTME: Scope step of the bulk change: hierarchy, optional branch and the conditions every object in scope matches.
// ABOUTME: Shows the scope preview its owner computes and lets the user go on only with 1 to 50,000 objects.

import {
    AddIcon,
    Alert,
    Autocomplete,
    Box,
    Button,
    Chip,
    DeleteIcon,
    IconButton,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from '@ds/coolshop';
import { SearchFilter, SearchFilterOperator } from '@src/__generated__/types';
import { ReactNode, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { useHierarchyList } from '../hooks/useBulkChangeNodes';
import { ScopePreviewState } from '../hooks/useScopePreview';
import { FindReplaceOptions } from '../types';
import {
    CODE_FIELD,
    conditionFieldInfo,
    ConditionRow,
    contentFieldKey,
    LIST_NAME_FIELD,
    operatorMessages,
    operatorsForType,
    STATUS_FIELD,
    STATUS_VALUES,
    statusMessages,
    valueEditorFor,
} from '../utils/conditionOperators';
import { ContentTypeLabeler } from '../utils/contentTypeLabels';
import { buildScope, hasIncompleteConditions, ScopeDraft } from '../utils/scopeDraft';
import { InfoNote } from './InfoNote';
import { NodeMultiPicker, nodeOptionLabel, NodePicker } from './NodePicker';
import { StepFrame } from './StepFrame';

type ScopeStepProps = {
    draft: ScopeDraft;
    onChange: (draft: ScopeDraft) => void;
    /** The hierarchy came with the request (node action, list view): it cannot change. */
    hierarchyLocked: boolean;
    options: FindReplaceOptions | null;
    optionsError: Error | null;
    labeler: ContentTypeLabeler;
    /** The count and first objects of the scope, computed by the owner of the draft. */
    scopePreview: ScopePreviewState;
    sidebar?: ReactNode;
    /** Without them the step renders no footer (the rule edit page). */
    onCancel?: () => void;
    onNext?: () => void;
};

const EMPTY_ROW: ConditionRow = { field: '', operator: '', values: [] };

/** The step may be left: a complete scope of 1 to MAX_SCOPE_NODES objects. */
export const canLeaveScope = (draft: ScopeDraft, { preview, loading }: ScopePreviewState) =>
    !!buildScope(draft) &&
    !hasIncompleteConditions(draft) &&
    !loading &&
    !!preview &&
    preview.count > 0 &&
    !preview.tooLarge;

export function ScopeStep({
    draft,
    onChange,
    hierarchyLocked,
    options,
    optionsError,
    labeler,
    scopePreview,
    sidebar,
    onCancel,
    onNext,
}: ScopeStepProps) {
    const { formatMessage } = useIntl();
    const { hierarchies } = useHierarchyList(false);
    const { preview, loading, error } = scopePreview;

    const contentTypes = useMemo(() => options?.contentTypes ?? [], [options]);

    const fieldOptions = useMemo(
        () => [
            {
                key: CODE_FIELD,
                label: formatMessage({ id: 'bulkChange.field.code', defaultMessage: 'Code' }),
            },
            {
                key: LIST_NAME_FIELD,
                label: formatMessage({ id: 'bulkChange.field.listName', defaultMessage: 'Name' }),
            },
            {
                key: STATUS_FIELD,
                label: formatMessage({ id: 'bulkChange.field.status', defaultMessage: 'Status' }),
            },
            ...contentTypes
                .map(({ ctg, ct }) => ({
                    key: contentFieldKey(ctg, ct),
                    label: `${labeler.groupLabel(ctg)} › ${labeler.typeLabel(ctg, ct)}`,
                }))
                .sort((a, b) => a.label.localeCompare(b.label)),
        ],
        [contentTypes, labeler, formatMessage],
    );

    const statusLabel = (status: string) =>
        statusMessages[status] ? formatMessage(statusMessages[status]) : status;

    const setRow = (index: number, row: ConditionRow) =>
        onChange({
            ...draft,
            conditions: draft.conditions.map((current, i) => (i === index ? row : current)),
        });

    const removeRow = (index: number) =>
        onChange({ ...draft, conditions: draft.conditions.filter((_row, i) => i !== index) });

    const removeLocked = (filter: SearchFilter) =>
        onChange({ ...draft, lockedFilters: draft.lockedFilters.filter(f => f !== filter) });

    const changeHierarchy = (hierarchyId: string) =>
        onChange({ hierarchyId, conditions: [], lockedFilters: [] });

    const renderValue = (row: ConditionRow, index: number) => {
        const info = conditionFieldInfo(row.field, contentTypes);
        const editor = valueEditorFor(info, row.operator);
        const contentType = contentTypes.find(c => contentFieldKey(c.ctg, c.ct) === row.field);
        const valueLabel = formatMessage({
            id: 'bulkChange.condition.value',
            defaultMessage: 'Value',
        });
        switch (editor) {
            case 'none':
                return null;
            case 'boolean':
                return (
                    <Select
                        label={valueLabel}
                        value={row.values[0] ?? ''}
                        onChange={e => setRow(index, { ...row, values: [e.target.value] })}
                        size="small"
                        fullWidth
                    >
                        <MenuItem value="true">
                            {formatMessage({
                                id: 'bulkChange.boolean.true',
                                defaultMessage: 'Yes',
                            })}
                        </MenuItem>
                        <MenuItem value="false">
                            {formatMessage({
                                id: 'bulkChange.boolean.false',
                                defaultMessage: 'No',
                            })}
                        </MenuItem>
                    </Select>
                );
            case 'options': {
                const choices =
                    row.field === STATUS_FIELD
                        ? STATUS_VALUES.map(value => ({ value, label: statusLabel(value) }))
                        : (contentType?.options ?? []).map(option => ({
                              value: option.value,
                              label: labeler.optionLabel(
                                  contentType?.ctg ?? '',
                                  contentType?.ct ?? '',
                                  option.value,
                              ),
                          }));
                return (
                    <Autocomplete<{ value: string; label: string }, true>
                        multiple
                        options={choices}
                        value={choices.filter(choice => row.values.includes(choice.value))}
                        getOptionLabel={choice => choice.label}
                        isOptionEqualToValue={(a, b) => a.value === b.value}
                        onChange={(_event, next) =>
                            setRow(index, { ...row, values: next.map(choice => choice.value) })
                        }
                        label={formatMessage({
                            id: 'bulkChange.condition.values',
                            defaultMessage: 'Values',
                        })}
                        size="small"
                        fullWidth
                    />
                );
            }
            case 'nodes':
                return (
                    <NodeMultiPicker
                        hierarchyId={contentType?.hierarchy ?? null}
                        label={formatMessage({
                            id: 'bulkChange.condition.values',
                            defaultMessage: 'Values',
                        })}
                        value={row.values}
                        onChange={ids => setRow(index, { ...row, values: ids })}
                    />
                );
            default:
                return (
                    <TextField
                        label={valueLabel}
                        type={editor === 'number' || editor === 'date' ? editor : 'text'}
                        value={row.values[0] ?? ''}
                        onChange={e => setRow(index, { ...row, values: [e.target.value] })}
                        size="small"
                        fullWidth
                    />
                );
        }
    };

    const lockedLabel = (filter: SearchFilter) => {
        // A node list's depth range ("min,max", max empty = unbounded), e.g. the "skip the root" one.
        if (filter.field === 'depth') {
            const [min, max] = (filter.value[0] ?? '').split(',');
            return max
                ? formatMessage(
                      { id: 'bulkChange.scope.depthRange', defaultMessage: 'Depth {min}–{max}' },
                      { min, max },
                  )
                : formatMessage(
                      { id: 'bulkChange.scope.depthFrom', defaultMessage: 'Depth {min} or more' },
                      { min },
                  );
        }
        return `${filter.field} ${formatMessage(
            operatorMessages[filter.operator],
        )} ${filter.value.join(', ')}`.trim();
    };

    const footer =
        onCancel || onNext ? (
            <>
                {onCancel && (
                    <Button variant="text" onClick={onCancel}>
                        {formatMessage({ id: 'bulkChange.cancel', defaultMessage: 'Cancel' })}
                    </Button>
                )}
                {onNext && (
                    <Button
                        variant="contained"
                        color="secondary"
                        data-testid="bulk-change-next-actions"
                        onClick={onNext}
                        disabled={!canLeaveScope(draft, scopePreview)}
                    >
                        {formatMessage({
                            id: 'bulkChange.scope.next',
                            defaultMessage: 'Next: actions',
                        })}
                    </Button>
                )}
            </>
        ) : undefined;

    return (
        <StepFrame
            title={formatMessage({
                id: 'bulkChange.scope.title',
                defaultMessage: 'Which objects should change?',
            })}
            subtitle={formatMessage({
                id: 'bulkChange.scope.subtitle',
                defaultMessage:
                    'Pick a hierarchy, optionally narrow it to a branch, then add conditions. An object is in scope when it matches all of them.',
            })}
            sidebar={sidebar}
            footer={footer}
        >
            <Box bgcolor="background.paper" border={1} borderColor="divider" borderRadius={2}>
                <Stack gap={2} p={2}>
                    <Stack direction="row" gap={1} alignItems="baseline">
                        <Typography variant="subtitle2">
                            {formatMessage({
                                id: 'bulkChange.scope.whereToLook',
                                defaultMessage: 'Where to look',
                            })}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {formatMessage({
                                id: 'bulkChange.scope.whereToLookHint',
                                defaultMessage: 'One hierarchy, optionally narrowed to a branch',
                            })}
                        </Typography>
                    </Stack>
                    <Stack direction="row" gap={2} alignItems="flex-end">
                        <Box width={260} flexShrink={0}>
                            <Select
                                label={formatMessage({
                                    id: 'bulkChange.scope.hierarchy',
                                    defaultMessage: 'Hierarchy',
                                })}
                                value={draft.hierarchyId}
                                onChange={e => changeHierarchy(e.target.value)}
                                disabled={hierarchyLocked}
                                fullWidth
                            >
                                {hierarchies.length === 0 && draft.hierarchyId && (
                                    <MenuItem value={draft.hierarchyId}>
                                        {draft.hierarchyId}
                                    </MenuItem>
                                )}
                                {hierarchies.map(hierarchy => (
                                    <MenuItem key={hierarchy.id} value={hierarchy.id}>
                                        {hierarchy.code}
                                    </MenuItem>
                                ))}
                            </Select>
                        </Box>
                        <Box flex={1} minWidth={0}>
                            <NodePicker
                                hierarchyId={draft.hierarchyId || null}
                                label={formatMessage({
                                    id: 'bulkChange.scope.branch',
                                    defaultMessage: 'Limit to branch (optional)',
                                })}
                                value={draft.rootNodeId ?? null}
                                onChange={(id, node) =>
                                    onChange({
                                        ...draft,
                                        rootNodeId: id ?? undefined,
                                        branchLabel: node ? nodeOptionLabel(node) : undefined,
                                    })
                                }
                            />
                        </Box>
                    </Stack>
                </Stack>
            </Box>

            {optionsError && (
                <Alert severity="error">
                    {formatMessage({
                        id: 'bulkChange.optionsError',
                        defaultMessage: 'Could not load the fields and languages.',
                    })}
                </Alert>
            )}

            <Box bgcolor="background.paper" border={1} borderColor="divider" borderRadius={2}>
                <Stack gap={2} p={2}>
                    <Stack direction="row" gap={1} alignItems="baseline">
                        <Typography variant="subtitle2">
                            {formatMessage({
                                id: 'bulkChange.scope.conditions',
                                defaultMessage: 'Conditions',
                            })}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {formatMessage({
                                id: 'bulkChange.scope.allMustMatch',
                                defaultMessage: 'All must match',
                            })}
                        </Typography>
                    </Stack>
                    {draft.lockedFilters.length > 0 && (
                        <Stack direction="row" gap={1} flexWrap="wrap">
                            {draft.lockedFilters.map(filter => (
                                <Chip
                                    key={JSON.stringify(filter)}
                                    label={lockedLabel(filter)}
                                    onDelete={() => removeLocked(filter)}
                                    data-testid="bulk-change-locked-filter"
                                />
                            ))}
                        </Stack>
                    )}
                    {draft.conditions.map((row, index) => {
                        const info = conditionFieldInfo(row.field, contentTypes);
                        return (
                            <Stack
                                // Rows have no identity of their own: their position is it.
                                // eslint-disable-next-line react/no-array-index-key
                                key={index}
                                direction="row"
                                gap={2}
                                alignItems="flex-end"
                                data-testid="bulk-change-condition"
                            >
                                <Box width={220} flexShrink={0}>
                                    <Select
                                        label={formatMessage({
                                            id: 'bulkChange.condition.field',
                                            defaultMessage: 'Field',
                                        })}
                                        value={row.field}
                                        onChange={e =>
                                            setRow(index, { ...EMPTY_ROW, field: e.target.value })
                                        }
                                        size="small"
                                        fullWidth
                                    >
                                        {fieldOptions.map(field => (
                                            <MenuItem key={field.key} value={field.key}>
                                                {field.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </Box>
                                <Box width={180} flexShrink={0}>
                                    <Select
                                        label={formatMessage({
                                            id: 'bulkChange.condition.operator',
                                            defaultMessage: 'Condition',
                                        })}
                                        value={row.operator}
                                        onChange={e =>
                                            setRow(index, {
                                                ...row,
                                                operator: e.target.value as SearchFilterOperator,
                                                values: [],
                                                language: undefined,
                                            })
                                        }
                                        disabled={!info}
                                        size="small"
                                        fullWidth
                                    >
                                        {(info ? operatorsForType(info.type) : []).map(operator => (
                                            <MenuItem key={operator} value={operator}>
                                                {formatMessage(operatorMessages[operator])}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </Box>
                                <Box flex={1} minWidth={0}>
                                    {renderValue(row, index)}
                                </Box>
                                <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => removeRow(index)}
                                    aria-label={formatMessage({
                                        id: 'bulkChange.condition.remove',
                                        defaultMessage: 'Remove the condition',
                                    })}
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </Stack>
                        );
                    })}
                    <Box>
                        <Button
                            variant="text"
                            startIcon={<AddIcon />}
                            disabled={!draft.hierarchyId}
                            onClick={() =>
                                onChange({ ...draft, conditions: [...draft.conditions, EMPTY_ROW] })
                            }
                        >
                            {formatMessage({
                                id: 'bulkChange.scope.addCondition',
                                defaultMessage: 'Add condition',
                            })}
                        </Button>
                    </Box>
                </Stack>
            </Box>

            {preview?.tooLarge && (
                <Alert severity="warning" data-testid="bulk-change-scope-too-large">
                    {formatMessage(
                        {
                            id: 'bulkChange.scope.tooLarge',
                            defaultMessage:
                                '{count, number} objects are in scope: a bulk change works on at most 50,000. Add conditions or limit it to a branch.',
                        },
                        { count: preview.count },
                    )}
                </Alert>
            )}
            {preview && !preview.tooLarge && preview.count === 0 && !loading && (
                <Alert severity="info" data-testid="bulk-change-scope-empty">
                    {formatMessage({
                        id: 'bulkChange.scope.empty',
                        defaultMessage: 'No object matches these conditions.',
                    })}
                </Alert>
            )}
            {error && <Alert severity="error">{error.message}</Alert>}

            <InfoNote
                title={formatMessage({
                    id: 'bulkChange.scope.sameFilters.title',
                    defaultMessage: 'Same filters as the list view',
                })}
                text={formatMessage({
                    id: 'bulkChange.scope.sameFilters.text',
                    defaultMessage:
                        'Conditions behave exactly like the column filters of the list. Starting a bulk change from the list carries its filters over here.',
                })}
            />
        </StepFrame>
    );
}
